const SECTION_SELECTOR = '[data-uc-routine]';

const setActive = (elements, index) => {
  for (const element of elements) {
    const value = element.getAttribute('data-uc-routine-media')
      ?? element.getAttribute('data-uc-routine-step')
      ?? element.getAttribute('data-uc-routine-product');

    element.classList.toggle('is-active', Number(value) === index);
  }
};

const setToggleState = (item, paused) => {
  const button = item.querySelector('[data-uc-routine-video-toggle]');
  if (!button) return;

  button.querySelector('.routine__video-toggle-icon--pause')?.classList.toggle('hidden', paused);
  button.querySelector('.routine__video-toggle-icon--play')?.classList.toggle('hidden', !paused);
  button.setAttribute('aria-label', paused ? 'Play video' : 'Pause video');
};

const initRoutine = (root) => {
  if (root.dataset.ucRoutineReady) return;
  root.dataset.ucRoutineReady = '1';

  const mediaItems = root.querySelectorAll('[data-uc-routine-media]');
  const steps = root.querySelectorAll('[data-uc-routine-step]');
  const products = root.querySelectorAll('[data-uc-routine-product]');
  const triggers = root.querySelectorAll('[data-uc-routine-trigger]');
  const startIndex = Number(root.dataset.ucRoutineStart) || 0;
  const locked = root.hasAttribute('data-uc-routine-lock');

  const stepContents = Array.from(steps, (step) => step.querySelector('.routine__step-content'));
  const shown = new Map();

  let progressIndex = startIndex;
  let progressFrame = 0;

  const setProgress = (index, percent) => {
    const content = stepContents[index];
    if (!content) return;

    const rounded = Math.round(percent * 100) / 100;
    if (shown.get(index) === rounded) return;

    shown.set(index, rounded);
    content.style.setProperty('--video-timeline-progress', `${rounded}%`);
  };

  const clearProgress = () => {
    for (const index of stepContents.keys()) setProgress(index, 0);
  };

  const stopProgress = () => {
    cancelAnimationFrame(progressFrame);
    progressFrame = 0;
  };

  const trackProgress = () => {
    const video = mediaItems[progressIndex]?.querySelector('video');
    if (!video || video.paused || video.ended) {
      if (video?.ended) setProgress(progressIndex, 0);
      progressFrame = 0;
      return;
    }

    const { duration, currentTime } = video;
    if (duration > 0 && Number.isFinite(duration)) {
      setProgress(progressIndex, Math.min(100, (currentTime / duration) * 100));
    }

    progressFrame = requestAnimationFrame(trackProgress);
  };

  const startProgress = (index) => {
    progressIndex = index;
    if (progressFrame) return;

    progressFrame = requestAnimationFrame(trackProgress);
  };

  const rewindAll = () => {
    for (const item of mediaItems) {
      const video = item.querySelector('video');
      if (!video) continue;

      item.removeAttribute('data-vmg-keep');
      video.pause();
      try {
        video.currentTime = 0;
      } catch (error) {
        /* metadata not ready */
      }
      setToggleState(item, true);
    }

    stopProgress();
    clearProgress();
  };

  const playActive = (index) => {
    const item = mediaItems[index];
    const video = item?.querySelector('video');
    if (!video) return;

    item.setAttribute('data-vmg-keep', '');
    window.videoMemoryGuard?.sweep();

    video.play().catch(() => {});
    setToggleState(item, false);
  };

  const activate = (index) => {
    setActive(mediaItems, index);
    setActive(steps, index);
    setActive(products, index);

    rewindAll();
    playActive(index);
  };

  for (const [index, item] of mediaItems.entries()) {
    const video = item.querySelector('video');
    if (!video) continue;

    video.dataset.vmgManual = '1';

    video.addEventListener('play', () => startProgress(index));
    video.addEventListener('pause', stopProgress);
    video.addEventListener('ended', () => {
      stopProgress();
      setProgress(index, 0);
    });

    if (locked) {
      video.loop = true;
      continue;
    }

    video.addEventListener('ended', () => {
      if (!item.classList.contains('is-active')) return;

      activate((index + 1) % mediaItems.length);
    });
  }

  for (const trigger of triggers) {
    trigger.addEventListener('click', (event) => {
      const index = Number(event.currentTarget.getAttribute('data-uc-routine-trigger'));
      if (Number.isNaN(index)) return;

      activate(index);
    });
  }

  for (const button of root.querySelectorAll('[data-uc-routine-video-toggle]')) {
    button.addEventListener('click', (event) => {
      const item = event.currentTarget.closest('.routine__media-item');
      const video = item?.querySelector('video');
      if (!video) return;

      if (video.paused) {
        video.play().catch(() => {});
        setToggleState(item, false);
      } else {
        video.pause();
        setToggleState(item, true);
      }
    });
  }

  rewindAll();
  playActive(startIndex);
};

for (const root of document.querySelectorAll(SECTION_SELECTOR)) {
  initRoutine(root);
}

document.addEventListener('shopify:section:load', (event) => {
  for (const root of event.target.querySelectorAll(SECTION_SELECTOR)) {
    initRoutine(root);
  }
});
